// EQuIP 2026 Provider Eligibility Criteria

export interface EligibilityCriterion {
  label: string;
  description: string;
}

export interface EligibilityExclusion {
  label: string;
  description: string;
}

export interface RoleEligibility {
  roleName: string;
  shortName: string;
  category: 'physician' | 'non-physician';
  criteria: EligibilityCriterion[];
  exclusions: EligibilityExclusion[];
  maxAward: {
    fullTime: number;
    perDay: number;
    perHour?: number;
  };
}

// Physician-specific criteria
const physicianCriteria: EligibilityCriterion[] = [
  {
    label: 'Active Eventus Employee',
    description: 'Must be an active employee at the start of the quarter',
  },
  {
    label: 'Eligible Discipline',
    description: 'Primary Care or Psychiatry',
  },
  {
    label: 'Productivity-Based Compensation',
    description: 'Must be on a productivity-based pay model',
  },
];

// APC-specific criteria
const apcCriteria: EligibilityCriterion[] = [
  {
    label: 'Active Eventus Employee',
    description: 'Must be an active employee at the start of the quarter',
  },
  {
    label: 'Eligible Discipline',
    description: 'Primary Care, Psychiatry, Palliative Care, or Teletriage',
  },
  {
    label: 'Productivity-Based Compensation',
    description: 'Must be on a productivity-based pay model',
  },
];

// Shared exclusion for salaried clinicians
const salariedExclusion: EligibilityExclusion = {
  label: 'Salaried Clinicians',
  description: 'Clinicians on salaried (non-productivity) compensation are not eligible',
};

export const eligibilityByRole: Record<string, RoleEligibility> = {
  // Physician
  'primary-care-physician': {
    roleName: 'Primary Care Physician',
    shortName: 'Physician',
    category: 'physician',
    criteria: physicianCriteria,
    exclusions: [salariedExclusion],
    maxAward: {
      fullTime: 4000,
      perDay: 800,
    },
  },
  'psychiatry-physician': {
    roleName: 'Psychiatrist',
    shortName: 'Psychiatrist',
    category: 'physician',
    criteria: physicianCriteria,
    exclusions: [salariedExclusion],
    maxAward: {
      fullTime: 4000,
      perDay: 800,
    },
  },
  // APCs
  'primary-care-apc': {
    roleName: 'Primary Care APC',
    shortName: 'PC APC',
    category: 'non-physician',
    criteria: apcCriteria,
    exclusions: [salariedExclusion],
    maxAward: {
      fullTime: 3000,
      perDay: 600,
    },
  },
  'psychiatry-apc': {
    roleName: 'Psychiatry APC',
    shortName: 'Psych APC',
    category: 'non-physician',
    criteria: apcCriteria,
    exclusions: [salariedExclusion],
    maxAward: {
      fullTime: 3000,
      perDay: 600,
    },
  },
  'palliative-care-apc': {
    roleName: 'Palliative Care APC',
    shortName: 'Pall APC',
    category: 'non-physician',
    criteria: apcCriteria,
    exclusions: [salariedExclusion],
    maxAward: {
      fullTime: 3000,
      perDay: 600,
    },
  },
  teletriage: {
    roleName: 'Teletriage',
    shortName: 'Teletriage',
    category: 'non-physician',
    criteria: apcCriteria,
    exclusions: [salariedExclusion],
    maxAward: {
      fullTime: 1500,
      perDay: 300,
      perHour: 37.5,
    },
  },
};

// Helper to get eligibility for physician track
export function getPhysicianEligibility(): RoleEligibility {
  return eligibilityByRole['primary-care-physician'];
}

// Helper to get eligibility for a specific APC role
export function getAPCEligibility(roleId: string): RoleEligibility | undefined {
  return eligibilityByRole[roleId];
}

// Get all APC eligibilities for comparison
export function getAllAPCEligibilities(): RoleEligibility[] {
  return Object.values(eligibilityByRole).filter((e) => e.category === 'non-physician');
}
